import type {
  ApiCompatibleTemplate,
  FormattedTemplate,
  QuestionnaireQuestionConfig,
  QuestionnaireSelectQuestionConfig,
  QuestionnaireScalarQuestionConfig,
  QuestionnaireScalarResponseType,
} from '../types/templates';

export const normalizeTemplateName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/\bformat\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Pick the default template.
 * If the user has a saved preference, look for an unseen template with the
 * same normalized name and a higher version. Otherwise keep saved preference.
 */
export const getDefaultTemplateId = (
  templates: FormattedTemplate[],
  savedPreferenceId?: string,
  seenTemplateIds?: string[]
): string => {
  if (savedPreferenceId) {
    const saved = templates.find((t) => t.id === savedPreferenceId);
    if (saved && !saved.isDisabled) {
      if (saved.isHostProvided) return saved.id;
      const seen = new Set(seenTemplateIds ?? []);
      const savedNorm = normalizeTemplateName(saved.name);
      const upgrade = templates
        .filter(
          (t) =>
            t.id !== saved.id &&
            !t.isLegacy &&
            !t.isHostProvided &&
            !seen.has(t.id) &&
            normalizeTemplateName(t.name) === savedNorm &&
            t.version &&
            saved.version &&
            t.version.localeCompare(saved.version, undefined, {
              numeric: true,
            }) > 0
        )
        .sort((a, b) =>
          (b.version ?? '').localeCompare(a.version ?? '', undefined, {
            numeric: true,
          })
        );
      if (upgrade.length > 0) return upgrade[0].id;
      return saved.id;
    }
  }
  return (
    (
      templates.find((t) => !t.isLegacy && !t.isDisabled) ??
      templates.find((t) => !t.isDisabled)
    )?.id ?? ''
  );
};

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

type TemplateSectionLike = {
  key?: unknown;
  label?: unknown;
  questions?: unknown;
};

export function normalizeQuestionOptions(options: unknown): string[] {
  return Array.isArray(options)
    ? options.filter(
        (option): option is string =>
          typeof option === 'string' && option.trim().length > 0
      )
    : [];
}

function hasBaseQuestionConfig(question: Record<string, unknown>): boolean {
  return (
    isNonEmptyString(question.questionId) &&
    isNonEmptyString(question.question) &&
    typeof question.required === 'boolean'
  );
}

export function isValidSelectQuestionConfig(
  question: unknown
): question is QuestionnaireSelectQuestionConfig {
  if (!isObject(question) || !hasBaseQuestionConfig(question)) {
    return false;
  }

  const options = normalizeQuestionOptions(question.answerOptions);

  return (
    ['radio', 'checkbox'].includes(question.selectionMode as string) &&
    options.length > 0
  );
}

const QUESTIONNAIRE_SCALAR_RESPONSE_TYPES: QuestionnaireScalarResponseType[] = [
  'text',
  'date',
  'number',
  'boolean',
];

export function isValidScalarQuestionConfig(
  question: unknown
): question is QuestionnaireScalarQuestionConfig {
  return (
    isObject(question) &&
    hasBaseQuestionConfig(question) &&
    QUESTIONNAIRE_SCALAR_RESPONSE_TYPES.includes(
      question.responseType as QuestionnaireScalarResponseType
    )
  );
}

export function isValidQuestionConfig(
  question: unknown
): question is QuestionnaireQuestionConfig {
  return (
    isValidSelectQuestionConfig(question) ||
    isValidScalarQuestionConfig(question)
  );
}

export function getTemplateSections(
  template: FormattedTemplate | null | undefined
): unknown[] {
  if (!template || !isObject(template.template)) {
    return [];
  }

  const structure = template.template.structure;
  if (!isObject(structure) || !Array.isArray(structure.sections)) {
    return [];
  }

  return structure.sections;
}

/**
 * Return the export-checkbox defaults declared by a template.
 *
 * `hasDefaults` is true when at least one section or question carries an
 * explicit `exportByDefault` field (either `true` or `false`). When true, the
 * caller should use the template's defaults instead of the user's saved
 * selectedDocumentFields for the current note load.
 *
 * `uncheckedKeys` is the set of field keys (section keys and questionnaire
 * questionIds) where `exportByDefault` is explicitly `false`. These start
 * unchecked; keys absent from this set start checked.
 *
 * Questionnaire sections: per-question `exportByDefault` on the question
 * config is used. The section-level `exportByDefault` is ignored for
 * questionnaire sections (questions have their own checkboxes).
 *
 * Non-questionnaire sections: the section-level `exportByDefault` is used.
 *
 * Returns `{ hasDefaults: false, uncheckedKeys: new Set() }` for a null or
 * malformed template, preserving today's behavior.
 */
export function getTemplateExportDefaults(
  template: FormattedTemplate | null | undefined
): { hasDefaults: boolean; uncheckedKeys: Set<string> } {
  const sections = getTemplateSections(template);
  if (sections.length === 0) {
    return { hasDefaults: false, uncheckedKeys: new Set() };
  }

  let hasDefaults = false;
  const uncheckedKeys = new Set<string>();

  for (const section of sections) {
    if (!isObject(section)) continue;

    if (section.type === 'questionnaire' && Array.isArray(section.questions)) {
      for (const question of section.questions) {
        if (!isObject(question)) continue;
        const qId = question.questionId;
        if (!isNonEmptyString(qId)) continue;
        if ('exportByDefault' in question) {
          hasDefaults = true;
          if (question.exportByDefault === false) {
            uncheckedKeys.add(qId);
          }
        }
      }
    } else {
      const sKey = section.key;
      if (isNonEmptyString(sKey) && 'exportByDefault' in section) {
        hasDefaults = true;
        if (section.exportByDefault === false) {
          uncheckedKeys.add(sKey);
        }
      }
    }
  }

  return { hasDefaults, uncheckedKeys };
}

export function getTemplateFieldLabels(
  template: FormattedTemplate | null | undefined
): Record<string, string> {
  return Object.fromEntries(
    getTemplateSections(template).flatMap((section) => {
      if (!isObject(section)) return [];

      if (
        section.type === 'questionnaire' &&
        Array.isArray(section.questions)
      ) {
        return section.questions.flatMap((question) => {
          if (!isValidQuestionConfig(question)) {
            return [];
          }

          return [
            [
              question.questionId,
              isNonEmptyString(question.label)
                ? question.label
                : question.question,
            ],
          ];
        });
      }

      const { key, label } = section as TemplateSectionLike;
      if (!isNonEmptyString(key) || !isNonEmptyString(label)) {
        return [];
      }

      return [[key, label]];
    })
  );
}

function getTemplateFieldOrder(
  template: FormattedTemplate | null | undefined
): string[] {
  const keys = getTemplateSections(template).flatMap((section) => {
    if (!isObject(section)) return [];

    if (section.type === 'questionnaire' && Array.isArray(section.questions)) {
      return section.questions.flatMap((question) =>
        isObject(question) && isNonEmptyString(question.questionId)
          ? [question.questionId]
          : []
      );
    }

    return isNonEmptyString(section.key) ? [section.key] : [];
  });

  return Array.from(new Set(keys));
}

/**
 * Reorder field keys to follow the template's section/question order.
 * Keys not in the template follow in their existing relative order.
 * Only reorders: never adds or removes keys.
 */
export function orderFieldKeysByTemplate(
  fieldKeys: string[],
  template: FormattedTemplate | null | undefined
): string[] {
  const templateOrder = getTemplateFieldOrder(template);
  const fieldKeySet = new Set(fieldKeys);
  const templateKeySet = new Set(templateOrder);

  return [
    ...templateOrder.filter((key) => fieldKeySet.has(key)),
    ...fieldKeys.filter((key) => !templateKeySet.has(key)),
  ];
}

export function isValidTemplate(
  template: unknown
): template is ApiCompatibleTemplate {
  if (!isObject(template)) return false;

  return isApiCompatible(template);
}

export function isApiCompatible(
  template: unknown
): template is ApiCompatibleTemplate {
  if (!isObject(template)) return false;

  if (
    !isNonEmptyString(template.name) ||
    !isNonEmptyString(template.description) ||
    !isNonEmptyString(template.version) ||
    !isNonEmptyString(template.customerId) ||
    !isNonEmptyString(template.createdBy)
  ) {
    return false;
  }

  if (!['SOAP_NOTE', 'CUSTOM'].includes(template.documentType as string)) {
    return false;
  }

  const structure = template.structure;
  if (!isObject(structure)) return false;

  if (
    ![
      'SOAP_NOTE',
      'PROGRESS_NOTE',
      'DISCHARGE_SUMMARY',
      'CONSULTATION',
      'ASSESSMENT',
      'CUSTOM',
    ].includes(structure.documentType as string)
  ) {
    return false;
  }

  const sections = structure.sections;
  if (!Array.isArray(sections) || sections.length === 0) return false;

  return sections.every((section) => {
    if (!isObject(section)) return false;

    if (section.type === 'questionnaire') {
      return (
        isNonEmptyString(section.key) &&
        typeof section.required === 'boolean' &&
        isNonEmptyString(section.prompt) &&
        Array.isArray(section.questions) &&
        section.questions.length > 0 &&
        section.questions.every((question) => isValidQuestionConfig(question))
      );
    }

    return (
      isNonEmptyString(section.key) &&
      [
        'boolean',
        'date',
        'text',
        'number',
        'stepper',
        'multi_select',
        'custom_template',
      ].includes(section.type as string) &&
      typeof section.required === 'boolean' &&
      isNonEmptyString(section.prompt)
    );
  });
}

const THREE_WEEKS_MS = 3 * 7 * 24 * 60 * 60 * 1000;

/**
 * Checks if a template was created within the last 3 weeks
 */
function isRecentlyCreated(createdAt?: number): boolean {
  if (!createdAt) return false;
  const now = Date.now();
  return now - createdAt < THREE_WEEKS_MS;
}

export function formatTemplateForUI(
  template: {
    name: string;
    documentType: string;
    description?: string;
    version?: string;
    isNew?: boolean;
    createdAt?: number; // Epoch timestamp in milliseconds from DynamoDB
    requiredContextKeys?: string[];
    scope?: FormattedTemplate['scope'];
  },
  id: string | number,
  isHostProvided: boolean = false,
  isLegacy: boolean = false
): FormattedTemplate {
  // Auto-calculate isNew based on createdAt if not explicitly set
  const isNew = template.isNew ?? isRecentlyCreated(template.createdAt);

  return {
    id: String(id),
    name: template.name,
    documentType: template.documentType || 'CUSTOM',
    selected: false,
    isHostProvided,
    isLegacy,
    isNew,
    scope: template.scope,
    version: template.version,
    description: template.description || '',
    template,
    requiredContextKeys: template.requiredContextKeys,
  };
}

/**
 * Detect pipeline version from template structure
 *
 * Detection rules:
 * - Has `pipeline: 'v2'` → V2
 * - Has `pipeline: 'v3'` → V3
 * - Everything else → V1 (default for backward compatibility)
 */
export function detectPipelineVersion(template: unknown): 'v1' | 'v2' | 'v3' {
  if (isObject(template) && template.pipeline === 'v2') {
    return 'v2';
  }

  if (isObject(template) && template.pipeline === 'v3') {
    return 'v3';
  }

  return 'v1';
}
